import React, { useCallback, useState } from 'react';

interface ImageUploaderProps {
  onImageSelected: (file: File, dataUrl: string) => void;
  disabled?: boolean;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  onImageSelected,
  disabled = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = useCallback((file: File) => {
    setError(null);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (PNG, JPG, JPEG, WEBP)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      return;
    }

    // Convert to data URL
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      onImageSelected(file, dataUrl);
    };
    reader.onerror = () => {
      setError('Failed to read image file');
    };
    reader.readAsDataURL(file);
  }, [onImageSelected]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled) return;

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  }, [disabled, handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  return (
    <div style={{ width: '100%' }}>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{
          border: `2px dashed ${isDragging ? '#007bff' : '#ccc'}`,
          borderRadius: '8px',
          padding: '40px 20px',
          textAlign: 'center',
          backgroundColor: isDragging ? '#f0f7ff' : '#fafafa',
          transition: 'all 0.2s ease',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <input
          type="file"
          accept="image/*"
          onChange={handleFileInput}
          disabled={disabled}
          style={{ display: 'none' }}
          id="image-upload"
        />
        
        <label
          htmlFor="image-upload"
          style={{
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'block',
          }}
        >
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>
            📁
          </div>
          
          <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
            {isDragging ? 'Drop image here' : 'Drag & drop image here'}
          </div>
          
          <div style={{ color: 'var(--tl4k-neutral-600)', marginBottom: '16px' }}>
            or click to browse
          </div>
          
          <div style={{ fontSize: '14px', color: 'var(--tl4k-neutral-500)' }}>
            Supported formats: PNG, JPG, JPEG, WEBP<br />
            Maximum size: 10MB
          </div>
        </label>
      </div>

      {error && (
        <div
          style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: 'var(--tl4k-surface-warm)',
            border: '1px solid #fcc',
            borderRadius: '4px',
            color: 'var(--tl4k-error-700)',
          }}
        >
          ⚠️ {error}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
